import { analyticsTrafficProperties } from "@tabbio/utils/analytics/browser-cookies";
import {
  browserMeasurement,
  browserMeasurementCookies,
} from "./browser-measurement.web";
import { postHogWebVital } from "@tabbio/utils/analytics/browser-context";
import {
  isPrivateAnalyticsPath,
  sanitizeAnalyticsProperties,
} from "@tabbio/utils/analytics/privacy";
import {
  isCriticalAnalyticsEvent,
  toCanonicalEventName,
} from "@tabbio/utils/analytics/events";
import { readCookieValue } from "@tabbio/utils/analytics/attribution";
import {
  POSTHOG_ANONYMOUS_ID_COOKIE,
  buildPostHogBatchBody,
  postHogBatchUrl,
  type PostHogQueuedPayload,
} from "@tabbio/utils/analytics/posthog";
import {
  createAnalyticsQueue,
  createBrowserIdleScheduler,
  installFlushOnPageHide,
  type AnalyticsQueue,
  type QueuedAnalyticsEvent,
} from "@tabbio/utils/analytics/queue";
import {
  getErrorObservabilityProperties,
  sanitizeObservabilityProperties,
  type ObservabilityProperties,
} from "./observability-properties";
import {
  addSentryBreadcrumb,
  captureSentryException,
  clearSentryUserContext,
  setSentryRouteContext,
  setSentryUserContext,
} from "./sentry";
import { isAppAuthRequiredError } from "./app-auth";
import {
  isProductAnalyticsEnabled,
  isMarketingEnabled,
  subscribePrivacyPreferences,
} from "./privacy-preferences";
import {
  buildGa4ProductEvent,
  buildGa4ScreenView,
  identifyGa4User,
  resetGa4User,
  sendGa4Event,
  startGa4ConsentSync,
  type Ga4QueuedEvent,
} from "./ga4.web";

export type ProductAnalyticsProperties = ObservabilityProperties;

let identifiedUserId: string | null = null;
let memoryAnonymousId: string | null = null;

function webDistinctId() {
  if (identifiedUserId) return identifiedUserId;
  const key = "tabbio_posthog_anonymous_id";
  try {
    // The marketing site's visitor id, when the person came from there: using
    // it here (and as `$anon_distinct_id` on identify) makes the landing-page
    // session and the signed-up person one PostHog profile.
    const fromMarketingSite = readCookieValue(
      document.cookie,
      POSTHOG_ANONYMOUS_ID_COOKIE,
    );
    if (fromMarketingSite) return fromMarketingSite;
    const existing = window.localStorage.getItem(key);
    if (existing) return existing;
    const created =
      memoryAnonymousId ??
      (typeof window.crypto?.randomUUID === "function"
        ? window.crypto.randomUUID()
        : `web-${Date.now()}-${Math.random().toString(36).slice(2)}`);
    memoryAnonymousId = created;
    window.localStorage.setItem(key, created);
    return created;
  } catch {
    return (memoryAnonymousId ??= globalThis.crypto.randomUUID());
  }
}

// ── PostHog destination ──────────────────────────────────────────────────────

/**
 * Read per payload rather than once at import: the value is build-time env in
 * production, but tests set it after importing. `__DEV__` is a Metro global,
 * guarded so a plain node run can import this module without defining it.
 */
function postHogEnvironment() {
  const isDev = typeof __DEV__ !== "undefined" && __DEV__;
  return (
    process.env.EXPO_PUBLIC_POSTHOG_ENVIRONMENT?.trim() ||
    process.env.SENTRY_ENVIRONMENT?.trim() ||
    (isDev ? "development" : "production")
  );
}

function postHogToken() {
  return process.env.EXPO_PUBLIC_POSTHOG_TOKEN?.trim() || null;
}

function postHogHost() {
  return (
    process.env.EXPO_PUBLIC_POSTHOG_HOST?.trim() || "https://us.i.posthog.com"
  );
}

/**
 * Whether PostHog may receive an event *right now*. Evaluated at enqueue time
 * and captured on the queued item, never at flush: an event that happened
 * before consent was granted must not be sent because a flush ran after.
 */
function postHogEligible() {
  return (
    typeof window !== "undefined" &&
    !isPrivateAnalyticsPath(window.location.href) &&
    isProductAnalyticsEnabled() &&
    Boolean(postHogToken()) &&
    !/^(1|true|yes|on)$/i.test(
      process.env.EXPO_PUBLIC_POSTHOG_DISABLED?.trim() ?? "",
    )
  );
}

function postHogPayload(
  properties: Record<string, unknown>,
  snapshot = false,
): PostHogQueuedPayload | undefined {
  if (!postHogEligible()) return undefined;
  return {
    distinctId: webDistinctId(),
    properties: {
      ...(snapshot ? properties : browserMeasurement.properties(properties)),
      environment: postHogEnvironment(),
      ...analyticsTrafficProperties(document.cookie ?? ""),
      surface: "app_web",
    },
  };
}

/**
 * One request per flush instead of one per event.
 *
 * `keepalive` lets the request outlive the document, which is what makes the
 * `pagehide` flush safe; browsers cap keepalive bodies at 64 KB, which the
 * queue's batch size of 25 keeps comfortably clear of.
 */
function sendPostHogBatch(
  batch: readonly QueuedAnalyticsEvent<PostHogQueuedPayload>[],
) {
  const token = postHogToken();
  if (!token || !batch.length) return;
  void fetch(postHogBatchUrl(postHogHost()), {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(buildPostHogBatchBody(token, batch)),
    keepalive: true,
  })
    .then((response) => {
      if (!response.ok) recordCollectionFailure("http");
    })
    .catch(() => recordCollectionFailure("network"));
}

// ── The queue ────────────────────────────────────────────────────────────────

type QueuedPayload = {
  readonly posthog?: PostHogQueuedPayload;
  readonly ga4?: Ga4QueuedEvent;
};

let queue: AnalyticsQueue<QueuedPayload> | null = null;

/**
 * Every product event passes through here.
 *
 * Before this, each call did a `fetch` to PostHog and a synchronous gtag push
 * inside whatever fired it. Now the call shapes the payloads (a snapshot of the
 * moment) and returns; an idle-time flush sends PostHog one `/batch/` request
 * for the whole batch and hands GA4 its pushes. Conversions and the pre-redirect
 * `checkout_started` are critical and still go out inside the call, because a
 * deferred callback is not guaranteed to run before the document is gone.
 */
function getQueue(): AnalyticsQueue<QueuedPayload> {
  if (queue) return queue;
  startBrowserLifecycle();
  queue = createAnalyticsQueue<QueuedPayload>({
    scheduler: createBrowserIdleScheduler(),
    sink: (batch) => {
      const forPostHog: QueuedAnalyticsEvent<PostHogQueuedPayload>[] = [];
      if (isPrivateAnalyticsPath(window.location.href)) return;
      for (const item of batch) {
        if (item.payload.posthog && postHogEligible()) {
          forPostHog.push({
            name: item.name,
            enqueuedAt: item.enqueuedAt,
            payload: item.payload.posthog,
          });
        }
        if (item.payload.ga4) sendGa4Event(item.payload.ga4);
      }
      sendPostHogBatch(forPostHog);
    },
  });
  installFlushOnPageHide(() => queue?.flush());
  return queue;
}

function enqueueProductEvent(
  name: string,
  properties: Record<string, unknown>,
  ga4: Ga4QueuedEvent | null,
  critical: boolean,
) {
  emitBrowserPage();
  const posthog = postHogPayload(properties);
  if (!posthog && !ga4) return;
  getQueue().enqueue(name, { posthog, ga4: ga4 ?? undefined }, { critical });
}

/** Drains deferred events now. Called before identity changes and on unload. */
export function flushProductAnalytics() {
  queue?.flush();
}

let lifecycleInstalled = false;
const collectionFailures: Record<string, number> = {};
function recordCollectionFailure(reason: "http" | "network") {
  collectionFailures[reason] = Math.min(
    1000,
    (collectionFailures[reason] ?? 0) + 1,
  );
}
export function getAnalyticsCollectionFailures() {
  return { ...collectionFailures };
}
function forgetBrowserIdentity() {
  browserMeasurement.reset();
  memoryAnonymousId = null;
  try {
    browserMeasurementCookies.writeCookie(POSTHOG_ANONYMOUS_ID_COOKIE, null, 0);
    window.localStorage.removeItem("tabbio_posthog_anonymous_id");
  } catch {
    /* In-memory consent still closes collection. */
  }
}
function emitBrowserPage() {
  if (!postHogEligible()) return;
  const { pageview, pageleave } = browserMeasurement.view();
  if (pageleave)
    getQueue().enqueue(
      "$pageleave",
      { posthog: postHogPayload(pageleave, true) },
      { critical: true },
    );
  if (pageview)
    getQueue().enqueue("$pageview", {
      posthog: postHogPayload(pageview, true),
    });
}
function startBrowserLifecycle() {
  if (lifecycleInstalled || typeof window === "undefined") return;
  lifecycleInstalled = true;
  let marketingWasEnabled = isMarketingEnabled();
  subscribePrivacyPreferences(() => {
    if (marketingWasEnabled && !isMarketingEnabled()) {
      queue?.clear();
      ++signupReceiptRevision;
    }
    marketingWasEnabled = isMarketingEnabled();
    if (isMarketingEnabled()) void trackConfirmedSignupConversion();
    if (!isProductAnalyticsEnabled()) {
      queue?.clear();
      forgetBrowserIdentity();
    } else emitBrowserPage();
  });
  window.addEventListener?.(
    "scroll",
    () => {
      if (postHogEligible()) browserMeasurement.updateScroll();
    },
    { passive: true },
  );
  window.addEventListener?.("pagehide", () => {
    if (postHogEligible()) {
      browserMeasurement.updateScroll();
      const pageleave = browserMeasurement.leave();
      if (pageleave)
        getQueue().enqueue(
          "$pageleave",
          { posthog: postHogPayload(pageleave, true) },
          { critical: true },
        );
    }
    flushProductAnalytics();
  });
  window.addEventListener?.("pageshow", (event) => {
    if (event.persisted) {
      lastScreen = null;
      trackProductScreen(window.location.pathname);
    }
  });
}
let lastScreen: string | null = null;

let signupReceiptPending = false;
let signupReceiptRevision = 0;
/** Called only after authentication; the API verifies the signup and atomically claims it. */
async function trackConfirmedSignupConversion() {
  const userId = identifiedUserId;
  if (
    !userId ||
    !isMarketingEnabled() ||
    signupReceiptPending ||
    isPrivateAnalyticsPath(window.location.href)
  )
    return;
  signupReceiptPending = true;
  const revision = signupReceiptRevision;
  try {
    const { api } = await import("./api");
    const response = await api.post<{
      event_id: string;
      signup_method: string;
    }>("/api/users/me/privacy-consent/signup-conversion");
    if (
      !response.error &&
      response.data &&
      revision === signupReceiptRevision &&
      identifiedUserId === userId &&
      isMarketingEnabled()
    ) {
      sendGa4Event({
        name: "signup_completed",
        params: {
          ...response.data,
          transaction_id: response.data.event_id,
          surface: "app_web",
        },
      });
    }
  } catch {
    recordCollectionFailure("network");
  } finally {
    signupReceiptPending = false;
  }
}

// ── Public API ───────────────────────────────────────────────────────────────

export function identifyProductUser(params: {
  userId: string;
  role?: string | null;
  locale?: string | null;
  companyId?: string | null;
  /** Person properties every funnel can be split by; never personal data. */
  properties?: Record<string, string | number | boolean | null>;
}) {
  // Anything captured under the anonymous id goes out first, so PostHog sees
  // the alias after the events it should attach to, not before.
  flushProductAnalytics();
  if (identifiedUserId && identifiedUserId !== params.userId) {
    identifiedUserId = null;
    ++signupReceiptRevision;
    forgetBrowserIdentity();
  }
  startBrowserLifecycle();

  const anonymousId =
    typeof window === "undefined" || !isProductAnalyticsEnabled()
      ? null
      : webDistinctId();
  identifiedUserId = params.userId;
  enqueueProductEvent(
    "$identify",
    {
      ...(anonymousId && anonymousId !== params.userId
        ? { $anon_distinct_id: anonymousId }
        : {}),
      $set: {
        role: params.role ?? undefined,
        language: params.locale === "ar" ? "ar" : "en",
        ...(params.properties ?? {}),
      },
    },
    null,
    true,
  );
  identifyGa4User(params);
  setSentryUserContext(params);
  void trackConfirmedSignupConversion();
}

export function resetProductUser() {
  flushProductAnalytics();
  // The auth tracker also calls this on anonymous startup and consent changes.
  // Only leaving an identified account should discard the anonymous journey.
  if (identifiedUserId !== null) {
    forgetBrowserIdentity();
    lastScreen = null;
  }
  identifiedUserId = null;
  ++signupReceiptRevision;
  resetGa4User();
  clearSentryUserContext();
}

export function trackProductScreen(
  pathname: string,
  properties?: ProductAnalyticsProperties,
) {
  if (isPrivateAnalyticsPath(pathname)) return;
  void trackConfirmedSignupConversion();

  const sanitized = sanitizeAnalyticsProperties(
    sanitizeObservabilityProperties(properties),
  );
  setSentryRouteContext(pathname, sanitized);
  addSentryBreadcrumb("screen_viewed", {
    pathname,
    ...sanitized,
  });
  // Consent Mode defaults to denied on every load, so the stored decision has
  // to be replayed before the first page view rather than only when the privacy
  // sheet is answered. Idempotent: the first call subscribes, the rest are
  // cheap no-ops.
  startGa4ConsentSync();
  startBrowserLifecycle();
  if (lastScreen !== pathname) {
    const ga4 = buildGa4ScreenView(pathname, sanitized);
    if (ga4) getQueue().enqueue("page_view", { ga4 });
    lastScreen = pathname;
  }
  emitBrowserPage();
}

export function trackProductEvent(
  event: string,
  properties?: ProductAnalyticsProperties,
) {
  // Signup's business fact is server-owned. Advertising receives a separate
  // server-confirmed receipt through trackConfirmedSignupConversion.
  if (event === "signup_completed") {
    void trackConfirmedSignupConversion();
    return;
  }

  // Sanitized first: `sanitizeObservabilityProperties` is what keeps emails,
  // names and URLs out of the payload, and Google's terms prohibit sending
  // personally identifiable data to GA4 at all.
  const sanitized = sanitizeAnalyticsProperties(
    sanitizeObservabilityProperties(properties),
  );
  startBrowserLifecycle();
  addSentryBreadcrumb(event, sanitized);
  enqueueProductEvent(
    event === "web_vitals" ? "$web_vitals" : event,
    event === "web_vitals" ? (postHogWebVital(sanitized) ?? {}) : sanitized,
    buildGa4ProductEvent(event, sanitized),
    isCriticalAnalyticsEvent(toCanonicalEventName(event)),
  );
}

export function captureProductException(
  exception: unknown,
  properties?: ProductAnalyticsProperties,
) {
  if (shouldSuppressExpectedAuthException(exception, properties)) {
    return;
  }

  captureSentryException(exception, {
    ...getErrorObservabilityProperties(exception),
    ...properties,
  });
}

export { getErrorObservabilityProperties };

function shouldSuppressExpectedAuthException(
  exception: unknown,
  properties?: ProductAnalyticsProperties,
) {
  if (!isAppAuthRequiredError(exception)) {
    return false;
  }

  const feature = String(properties?.feature ?? "");
  const action = String(properties?.action ?? "");
  const source = String(properties?.source ?? "");
  const route = String(properties?.route ?? "");

  return (
    feature === "subscription" &&
    (action === "sync" ||
      source === "automatic" ||
      source === "customer_info" ||
      route === "/login" ||
      route === "/auth-complete" ||
      route === "/resume-import" ||
      route === "/onboarding")
  );
}
